import { createFinishStepContent } from '../FinishStepContent';
import { closeSidebarPanel, openDataPlatformSubmenu } from '../sidebarHelpers';

const createNewSegment = (baseUrl, navigate, user) => {
  const ensureRuleSelectOpen = (btnSelector) => {
    const input = document.querySelector(`${btnSelector} input`);

    const event = new KeyboardEvent('keydown', {
      key: 'ArrowDown',
      code: 'ArrowDown',
      keyCode: 40,
      bubbles: true,
    });

    input?.dispatchEvent(event);
  };

  const ensureDropdownOpen = (btnSelector) => {
    const dropdown = document.querySelector(`${btnSelector} .dropdown-menu`);
    if (!dropdown || dropdown.style.opacity === '0') {
      document.querySelector(`${btnSelector} .dropdown-toggle`)?.click();
    }
  };

  return {
    id: 'createNewSegment',
    title: 'Create a new segment',
    steps: [
      {
        selector: '[data-tour="dataPlatformLink"]',
        content: <p>Click on the "Data Platform" menu item in the navigation.</p>,
      },
      {
        actionBefore: () => openDataPlatformSubmenu('[data-tour="segmentsLink"]'),
        selector: '[data-tour="segmentsLink"]',
        content: <p>Click on the "Segments" in the navigation</p>,
      },
      {
        actionBefore: () => {
          closeSidebarPanel();
          navigate(`${baseUrl}/segments`, true);
        },
        selector: '[data-tour="listingPageCards"]',
        content: (
          <p>
            The Segment is a cohort of users grouped by one or multiple criteria. We have already created some for your
            convenience but you can also create your own segments.
          </p>
        ),
      },
      {
        selector: '[data-tour="newElementBtn"]',
        content: <p>Click on the "New Segment" button</p>,
      },
      {
        actionBefore: ({ currentStep, setCurrentStep }) => {
          navigate(`${baseUrl}/segments/new`, true);
          const cb = (e) => {
            const target = e.target.closest('[data-tour="segmentAddRuleBtn"]');
            if (target) {
              setCurrentStep(currentStep + 1);
              document.removeEventListener('click', cb);
            }
          };
          document.addEventListener('click', cb);
        },
        selector: '[data-tour="segmentAddRuleBtn"]',
        content: <p>Click on the Add rule dropdown to see the list of all the rules you can choose from. </p>,
        stepInteraction: true,
      },
      {
        selector: '[data-tour="segmentAddRuleBtn"] .mantine-Combobox-dropdown',
        content: <p>Each rule you add helps group the users with similar behavior.</p>,
        actionBefore: async () => {
          // Always click the button to open the dropdown
          const btn = document.querySelector('[data-tour="segmentAddRuleBtn"] button');
          btn?.click();
          // Wait for dropdown to render
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      },
      {
        selector: '[data-tour="segmentAddRuleBtn"] .mantine-Combobox-option',
        content: <p>Select a rule like "Viewed product" to add it to your segment.</p>,
        actionBefore: async () => {
          // Ensure dropdown is open
          if (!document.querySelector('[data-tour="segmentAddRuleBtn"] .mantine-Combobox-dropdown')) {
            const btn = document.querySelector('[data-tour="segmentAddRuleBtn"] button');
            btn?.click();
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        },
      },
      {
        selector: '[data-rule-name="Viewed product"]',
        content: <p>Each rule has settings you can adjust.</p>,
        actionBefore: async () => {
          // Click on "Viewed product" option in the dropdown
          const options = document.querySelectorAll('[data-tour="segmentAddRuleBtn"] .mantine-Combobox-option');
          for (const option of options) {
            if (option.textContent.includes('Viewed product')) {
              option.click();
              break;
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      },
      {
        selector: '[data-tour="segmentModeSelect"]',
        content: <p>Mode is binary and it can either be applied in its positive or negative form.</p>,
      },
      {
        selector: '[data-tour="segmentPageFilters"] button',
        content: <p>Click on Add Filters button to see the available filter options.</p>,
      },
      {
        selector: '[data-tour="segmentPageFilters"]',
        content: <p>Each rule will have different filter options. Select the filter called "Count".</p>,
        actionBefore: async () => {
          const btn = document.querySelector('[data-tour="segmentPageFilters"] button');
          btn?.click();
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
        stepInteraction: true,
      },
      {
        selector: '[data-tour="segmentSelectedFilterElements"]',
        content: <p>With this filter, you can filter users who have looked at multiple products on your website.</p>,
        actionBefore: async () => {
          // Click on "Count" option in the dropdown if it's still open
          const options = document.querySelectorAll('.mantine-Combobox-dropdown .mantine-Combobox-option');
          for (const option of options) {
            if (option.textContent.includes('Count')) {
              option.click();
              break;
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      },
      {
        selector: '[data-tour="segmentSelectedFilterElements"]',
        stepInteraction: true,
        content: <p>Add a number in the Count from field to see how the estimated number below changes.</p>,
      },
      {
        selector: '[data-tour="segmentCalculateBtn"]',
        content: <p>Click on the "Calculate Segment" button to see the estimated number of profiles.</p>,
      },
      {
        selector: '[data-tour="segmentEstimatedProfiles"]',
        stepInteraction: true,
        content: (
          <>
            <p>This is an estimate of how many people will be included in this segment at the current time.</p>
            <p>Please note that this number also includes anonymous profiles.</p>
            <p>
              Each segment is recalculated once every hour and new users are included if they match the rules above.
            </p>
            <p>
              The number in the brackets shows the estimated users you can reach by channel: email, Viber, SMS, and web
              push notifications, these are the numbers of users subscribed for your marketing.
            </p>
          </>
        ),
        actionBefore: () => document.querySelector('[data-tour="segmentCalculateBtn"]')?.click(),
      },
      {
        selector: '[data-tour="segmentSaveBtn"]',
        content: <p>Click on the "Save" button.</p>,
      },
      {
        selector: '[data-tour="listingPageCards"]',
        content: <p>Now the segment is saved and you can use it with campaigns and automations.</p>,
        actionBefore: () => document.querySelector('[data-tour="segmentSaveBtn"]')?.click(),
      },
      {
        actionBefore: () => navigate(`${baseUrl}/guide`, true),
        selector: '[data-tour="userGuideHeading"]',
        content: createFinishStepContent(),
      },
    ],
    isComplete: !!user.userActions.find((a) => a.action === 'taskCompleted' && a.context.task === 'createNewSegment'),
  };
};

export default createNewSegment;
